/**
 * Universal command palette (issue #1515, EPIC #1512) — the "Google of the
 * platform" UI: ONE ranked, federated, anti-confusion list that merges the
 * client command registry (instant "go to" navigation + quick actions, #1514)
 * with the server federated search (records + documents, #1513).
 *
 * A cmdk-style modal mounted once in each app shell (admin + CRM). Opened by
 * Cmd/Ctrl-K or the header search affordance. Anti-confusion rules:
 *
 *  1. ONE vertical grouped list in a stable logical order (Go to · Actions ·
 *     records · documents). Each group = uppercase header + count.
 *  2. The single BEST overall match across ALL groups is preselected — client
 *     and server scores are reconciled onto one unified [0, 1] scale.
 *  3. Triple-redundant type cues on every row: section header + a consistent
 *     leading Lucide icon + a trailing verb ("Go" / "Run" / "Open"), with the
 *     ↵ glyph on the active row.
 *  4. Matched-substring highlighting via the shared {field,start,length} offsets.
 *  5. Mandatory context sublabel on every row.
 *  6. "See all N in <group>" deep link for server groups that overflow the cap.
 *  7. Rich empty state: recent items + suggested destinations + top actions
 *     (NO API call on an empty/short query).
 *  8. Scope tabs (All · Pages · Records · Documents · Actions) + inline ">"
 *     prefix → Actions-only.
 *  9. Keyboard-first across ALL groups (↑↓ wrap, ↵ open/run, esc close).
 * 10. Mobile bottom-sheet, dark mode, ≥44px targets, agency-neutral, emoji-free.
 *
 * Built without an external dependency (no cmdk package) to avoid adding a
 * supply-chain surface that runs in CI on the prod box.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Loader2,
  Users,
  Building2,
  UserSearch,
  FileSignature,
  Sprout,
  CheckSquare,
  Scale,
  FileText,
  CornerDownLeft,
  History,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/auth";
import { useCommandRegistry } from "../../lib/commands";
import type {
  CommandEntry,
  CommandMatch,
  LucideComponent,
} from "../../lib/commands";
import {
  globalSearch,
  type SearchEntityType,
  type SearchGroup,
  type SearchMatch,
  type SearchResult,
} from "../../api/search";
import { useRecentItems, type RecentItem } from "../../hooks/useRecentItems";
import { SearchSpot } from "../illustrations";

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;
const MAX_SUGGESTED_PAGES = 5;
const MAX_TOP_ACTIONS = 4;

// ---- type cues ------------------------------------------------------------
//
// A consistent leading icon per row type (cue (b) of the triple-redundant
// scheme). Nav uses a Compass (destinations), actions a Zap (do-something);
// records/documents reuse the app's existing module semantics.
const RECORD_ICON: Record<SearchEntityType, LucideIcon> = {
  contact: Users,
  listing: Building2,
  buyer: UserSearch,
  deal: FileSignature,
  farm: Sprout,
  task: CheckSquare,
  conveyancer: Scale,
  template: FileText,
  document: FileText,
};

/**
 * The record type named ON each server row (#4315). A deal and the listing it
 * sits on share the same property-address label, so the section header alone
 * left two identical-looking rows; the pill says which one the row opens.
 */
const RECORD_TYPE_LABEL: Record<SearchEntityType, string> = {
  contact: "Contact",
  listing: "Listing",
  buyer: "Buyer",
  deal: "Deal",
  farm: "Farm",
  task: "Task",
  conveyancer: "Conveyancer",
  template: "Template",
  document: "Document",
};

/** A trailing verb affordance per row kind (cue (c)). */
type RowKind = "nav" | "action" | "record" | "document";

const VERB: Record<RowKind, string> = {
  nav: "Go",
  action: "Run",
  record: "Open",
  document: "Open",
};

// Which server entity types fold under "Records" vs "Documents" for the scope
// tabs (server groups stay their own visual groups; this only drives filtering).
const DOCUMENT_TYPES = new Set<SearchEntityType>(["template", "document"]);
const RECORD_TYPES = new Set<SearchEntityType>([
  "contact",
  "listing",
  "buyer",
  "deal",
  "farm",
  "task",
  "conveyancer",
]);

type Scope = "all" | "pages" | "records" | "documents" | "actions";

const SCOPE_TABS: { id: Scope; label: string }[] = [
  { id: "all", label: "All" },
  { id: "pages", label: "Pages" },
  { id: "records", label: "Records" },
  { id: "documents", label: "Documents" },
  { id: "actions", label: "Actions" },
];

// ---- unified row model ----------------------------------------------------
//
// Both sources feed ONE flat list so a single best overall match can be
// preselected and keyboard nav can wrap across every group.
interface Row {
  /** Stable key within the palette. */
  key: string;
  kind: RowKind;
  /** Group bucket id: "nav" | "action" | a SearchEntityType. */
  groupId: string;
  icon: LucideIcon | LucideComponent;
  label: string;
  sublabel: string;
  /** Highlight offsets on the label (or null). */
  match: CommandMatch | SearchMatch | null;
  /** Unified relevance — HIGHER is better (see UNIFIED RANKING below). */
  unified: number;
  /** Navigate to this url (records/nav). */
  url?: string;
  /** Run this side-effect (actions). */
  run?: () => void;
  /** Recents bookkeeping for records/nav. */
  recent?: RecentItem;
  /** Server record type pill ("Deal", "Listing", ...) — #4315. */
  typeLabel?: string;
}

/**
 * UNIFIED RANKING — reconciling two opposite score conventions.
 *
 * The client registry returns a TIER where LOWER is better
 * (0=label-prefix, 1=label-substring, 2=fuzzy, 3=sublabel-substring); the
 * backend returns a relevance in [0, 1] where HIGHER is better. To preselect
 * the single best match across BOTH, we map everything onto one descending
 * [0, 1] scale: the backend score is used directly; the registry tier maps to
 * a hand-tuned descending value (prefix ≈ 1.0, substring ≈ 0.85, fuzzy ≈ 0.6,
 * sublabel ≈ 0.5) chosen so an exact instant nav/action hit out-ranks a
 * fuzzy/related server hit, while a strong server hit still beats a weak
 * sublabel-only registry hit. An empty-query registry match (score 0, no
 * field) is given 0 so it never wins the preselect.
 */
const REGISTRY_TIER_TO_UNIFIED: Record<number, number> = {
  0: 1.0, // label prefix
  1: 0.85, // label substring
  2: 0.6, // fuzzy / subsequence
  3: 0.5, // sublabel substring
};

function registryUnified(score: number, hasMatch: boolean): number {
  if (!hasMatch) return 0; // empty-query passthrough — not a real match
  return REGISTRY_TIER_TO_UNIFIED[score] ?? 0.4;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/** Split a label into [before, match, after] for bold highlighting. */
function highlight(
  label: string,
  match: CommandMatch | SearchMatch | null,
): React.ReactNode {
  if (!match || match.field !== "label" || match.length <= 0) return label;
  const { start, length } = match;
  if (start < 0 || start >= label.length) return label;
  const before = label.slice(0, start);
  const hit = label.slice(start, start + length);
  const after = label.slice(start + length);
  return (
    <>
      {before}
      <span className="font-semibold text-[var(--color-text-primary)]">
        {hit}
      </span>
      {after}
    </>
  );
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { commands, filter } = useCommandRegistry();
  const { recents, add: addRecent, clear: clearRecents } = useRecentItems(
    user?.id,
  );

  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<Scope>("all");
  const [serverGroups, setServerGroups] = useState<SearchGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Admins reach a deal (and any shell-routed record) via the admin shell.
  const resolveUrl = useCallback(
    (type: string, id: number | string, url: string): string => {
      if (type === "deal" && user?.role === "admin") {
        return `/admin/deals/${id}`;
      }
      return url;
    },
    [user?.role],
  );

  // The inline ">" prefix forces Actions-only scope; everything after it is the
  // effective query. (The scope tabs still work for non-prefixed queries.)
  const isActionPrefix = query.trimStart().startsWith(">");
  const effectiveQuery = (
    isActionPrefix ? query.trimStart().slice(1) : query
  ).trim();
  const effectiveScope: Scope = isActionPrefix ? "actions" : scope;

  // Reset transient state whenever the palette opens, and focus the input.
  useEffect(() => {
    if (open) {
      setQuery("");
      setScope("all");
      setServerGroups([]);
      setError(false);
      setActiveIndex(0);
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  // Server search — only for a non-empty effective query at/above the min
  // length AND when the scope can show server results (records/documents/all).
  // An empty query (the rich empty state) NEVER hits the API.
  const scopeWantsServer =
    effectiveScope === "all" ||
    effectiveScope === "records" ||
    effectiveScope === "documents";

  useEffect(() => {
    if (!open) return;
    if (!scopeWantsServer || effectiveQuery.length < MIN_QUERY_LENGTH) {
      abortRef.current?.abort();
      setServerGroups([]);
      setLoading(false);
      setError(false);
      return;
    }

    setLoading(true);
    setError(false);
    const handle = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      globalSearch(effectiveQuery, controller.signal)
        .then((resp) => {
          setServerGroups(resp.groups);
        })
        .catch((err) => {
          if ((err as Error)?.name === "AbortError") return;
          setError(true);
          setServerGroups([]);
        })
        .finally(() => {
          if (abortRef.current === controller) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(handle);
  }, [effectiveQuery, scopeWantsServer, open]);

  // ---- client registry rows (instant, no network) ------------------------
  const registryRows = useMemo<{ nav: Row[]; action: Row[] }>(() => {
    const nav: Row[] = [];
    const action: Row[] = [];
    if (effectiveQuery.length === 0) return { nav, action };

    const ranked = filter(effectiveQuery);
    for (const m of ranked) {
      const entry: CommandEntry = m.entry;
      const unified = registryUnified(m.score, !!m.match);
      if (entry.kind === "nav") {
        nav.push({
          key: `nav:${entry.id}`,
          kind: "nav",
          groupId: "nav",
          icon: entry.icon,
          label: entry.label,
          sublabel: entry.sublabel,
          match: m.match ?? null,
          unified,
          url: entry.url,
          recent: {
            id: entry.id,
            type: "nav",
            label: entry.label,
            sublabel: entry.sublabel,
            url: entry.url,
          },
        });
      } else {
        action.push({
          key: `action:${entry.id}`,
          kind: "action",
          groupId: "action",
          icon: entry.icon,
          label: entry.label,
          sublabel: entry.sublabel ?? "",
          match: m.match ?? null,
          unified,
          run: entry.run,
        });
      }
    }
    return { nav, action };
  }, [filter, effectiveQuery]);

  // ---- server rows (records + documents) ---------------------------------
  const serverRows = useMemo<Map<string, Row[]>>(() => {
    const byType = new Map<string, Row[]>();
    for (const group of serverGroups) {
      const rows: Row[] = group.results.map((r: SearchResult) => {
        const isDoc = DOCUMENT_TYPES.has(r.type);
        const url = resolveUrl(r.type, r.id, r.url);
        return {
          key: `${r.type}:${r.id}`,
          kind: isDoc ? "document" : "record",
          groupId: r.type,
          icon: RECORD_ICON[r.type] ?? FileText,
          label: r.label,
          sublabel: r.sublabel,
          match: r.match,
          unified: r.score,
          url,
          typeLabel: RECORD_TYPE_LABEL[r.type],
          recent: {
            id: r.id,
            type: r.type,
            label: r.label,
            sublabel: r.sublabel,
            url,
          },
        };
      });
      byType.set(group.type, rows);
    }
    return byType;
  }, [serverGroups, resolveUrl]);

  // ---- scope filtering -----------------------------------------------------
  const showNav = effectiveScope === "all" || effectiveScope === "pages";
  const showActions = effectiveScope === "all" || effectiveScope === "actions";

  // ---- ordered visual sections (stable logical order) ---------------------
  interface Section {
    id: string;
    title: string;
    rows: Row[];
    /** Server group metadata for the "See all N" affordance. */
    seeAll?: { total: number; shown: number; url: string };
  }

  const sections = useMemo<Section[]>(() => {
    const serverGroupVisible = (type: SearchEntityType): boolean => {
      if (effectiveScope === "all") return true;
      if (effectiveScope === "records") return RECORD_TYPES.has(type);
      if (effectiveScope === "documents") return DOCUMENT_TYPES.has(type);
      return false;
    };

    const out: Section[] = [];
    if (showNav && registryRows.nav.length > 0) {
      out.push({ id: "nav", title: "Go to", rows: registryRows.nav });
    }
    if (showActions && registryRows.action.length > 0) {
      out.push({ id: "action", title: "Actions", rows: registryRows.action });
    }
    // Server groups keep the backend's stable order.
    for (const group of serverGroups) {
      if (!serverGroupVisible(group.type)) continue;
      const rows = serverRows.get(group.type) ?? [];
      if (rows.length === 0) continue;
      out.push({
        id: group.type,
        title: group.label,
        rows,
        seeAll:
          group.total_matches > rows.length && group.see_all_url
            ? {
                total: group.total_matches,
                shown: rows.length,
                url: group.see_all_url,
              }
            : undefined,
      });
    }
    return out;
  }, [effectiveScope, showNav, showActions, registryRows, serverGroups, serverRows]);

  // Flat list for keyboard nav + preselection (sections already in order).
  const flatRows = useMemo<Row[]>(
    () => sections.flatMap((s) => s.rows),
    [sections],
  );

  // Preselect the single best overall match (highest unified score) ONLY when
  // the candidate SET genuinely changes — NOT on every render.
  //
  // `flatRows` (→ sections → registryRows/serverRows) is recreated with a fresh
  // array identity on every render because its upstream memo deps (`filter`
  // from useCommandRegistry, derived `serverGroups`) are not referentially
  // stable. Keying the preselect effect on `flatRows` therefore fired
  // continuously and clobbered any user navigation (ArrowDown/ArrowUp or hover)
  // back to the best match on the very next render (#1515).
  //
  // The fix is render-identity-immune: derive a stable SIGNATURE string from
  // the ordered row keys + the effective query. The preselect only runs when
  // that signature changes (a real new result set); when it is unchanged we
  // merely clamp activeIndex into range, never forcing it back to best, so the
  // user's keyboard/hover selection HOLDS across the churning re-renders.
  const rowsSignature = useMemo(
    () => `${effectiveQuery} ${flatRows.map((r) => r.key).join("")}`,
    [effectiveQuery, flatRows],
  );
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastSignatureRef.current === rowsSignature) {
      // Same candidate set across a churning re-render: HOLD the user's
      // selection. Only clamp into range if the row count shrank.
      if (activeIndex >= flatRows.length) {
        setActiveIndex(flatRows.length === 0 ? 0 : flatRows.length - 1);
      }
      return;
    }
    lastSignatureRef.current = rowsSignature;

    if (flatRows.length === 0) {
      setActiveIndex(0);
      return;
    }
    let best = 0;
    let bestScore = flatRows[0]!.unified;
    for (let i = 1; i < flatRows.length; i++) {
      if (flatRows[i]!.unified > bestScore) {
        bestScore = flatRows[i]!.unified;
        best = i;
      }
    }
    setActiveIndex(best);
  }, [rowsSignature, flatRows, activeIndex]);

  // Keep the active row scrolled into view. Keyed on the stable signature (not
  // the churning `flatRows` identity) so it fires on a real selection/result
  // change, not on every re-render.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`,
    );
    el?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, rowsSignature]);

  const activateRow = useCallback(
    (row: Row | undefined) => {
      if (!row) return;
      if (row.run) {
        onClose();
        row.run();
        return;
      }
      if (row.url) {
        if (row.recent) addRecent(row.recent);
        onClose();
        navigate(row.url);
      }
    },
    [navigate, onClose, addRecent],
  );

  const goRecent = useCallback(
    (item: RecentItem) => {
      addRecent(item);
      onClose();
      navigate(item.url);
    },
    [navigate, onClose, addRecent],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (flatRows.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatRows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatRows.length) % flatRows.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      activateRow(flatRows[activeIndex]);
    }
  };

  if (!open) return null;

  const isEmptyQuery = effectiveQuery.length === 0;
  const isShortQuery =
    !isEmptyQuery && effectiveQuery.length < MIN_QUERY_LENGTH;
  const showEmptyState = isEmptyQuery;
  const showNoResults =
    !loading &&
    !error &&
    !isEmptyQuery &&
    !isShortQuery &&
    flatRows.length === 0;

  // Empty-state suggestions: top nav destinations + top actions from the
  // (already RBAC-gated) registry, in registry order.
  const suggestedPages = commands
    .filter((c): c is Extract<CommandEntry, { kind: "nav" }> => c.kind === "nav")
    .slice(0, MAX_SUGGESTED_PAGES);
  const topActions = commands
    .filter(
      (c): c is Extract<CommandEntry, { kind: "action" }> =>
        c.kind === "action",
    )
    .slice(0, MAX_TOP_ACTIONS);

  // Running flat index so keyboard nav lines up across sections.
  let runningIndex = -1;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-start justify-center bg-black/40 p-0 sm:p-4 sm:pt-[12vh]"
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Universal search"
        className="bg-[var(--color-bg-card)] rounded-t-xl sm:rounded-lg w-full sm:max-w-xl max-h-[88vh] sm:max-h-[72vh] flex flex-col overflow-hidden shadow-xl border border-[var(--color-border)]"
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 border-b border-[var(--color-border)]">
          <Search className="h-5 w-5 text-[var(--color-text-muted)] shrink-0" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, actions, contacts, listings, deals, documents…"
            aria-label="Search query"
            className="flex-1 bg-transparent py-3.5 text-base text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && (
            <Loader2 className="h-4 w-4 animate-spin text-[var(--color-text-muted)] shrink-0" />
          )}
        </div>

        {/* Scope tabs */}
        <div
          role="tablist"
          aria-label="Search scope"
          className="flex items-center gap-1 px-3 py-2 border-b border-[var(--color-border)] overflow-x-auto"
        >
          {SCOPE_TABS.map((tab) => {
            const selected = effectiveScope === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                disabled={isActionPrefix && tab.id !== "actions"}
                onClick={() => setScope(tab.id)}
                className={`inline-flex items-center justify-center min-h-[44px] sm:min-h-[32px] rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  selected
                    ? "bg-[var(--color-navy)] text-white"
                    : "text-[var(--color-text-muted)] hover:bg-[var(--color-bg-page)]"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Results / states */}
        <div ref={listRef} className="flex-1 overflow-y-auto py-2">
          {isShortQuery && (
            <p className="px-4 py-6 text-sm text-[var(--color-text-muted)] text-center">
              Keep typing — at least {MIN_QUERY_LENGTH} characters to search
              records.
            </p>
          )}

          {error && (
            <p className="px-4 py-6 text-sm text-[var(--color-danger)] text-center">
              Search is unavailable right now. Please try again.
            </p>
          )}

          {showNoResults && (
            // #2580: the universal-search no-results state gets the custom
            // search spot illustration (a searched-but-nothing-matched moment).
            <div className="flex flex-col items-center px-4 py-8 text-center">
              <SearchSpot className="mb-2" />
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                No results for &ldquo;{effectiveQuery}&rdquo;
              </p>
              <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                Try a different term, or check the spelling.
              </p>
            </div>
          )}

          {/* Rich empty state: recents + suggested pages + top actions. */}
          {showEmptyState && (
            <div className="pb-1">
              {recents.length > 0 && (
                <EmptySection
                  title="Recent"
                  action={
                    <button
                      type="button"
                      onClick={clearRecents}
                      className="min-h-[44px] px-2 -mr-2 text-xs font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors normal-case tracking-normal"
                      aria-label="Clear search history"
                    >
                      Clear
                    </button>
                  }
                >
                  {recents.map((item) => (
                    <button
                      key={`recent:${item.url}`}
                      type="button"
                      onClick={() => goRecent(item)}
                      className="w-full min-h-[44px] flex items-center gap-3 px-4 py-2 text-left hover:bg-[var(--color-bg-page)] transition-colors"
                    >
                      <History className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-[var(--color-text-primary)]">
                          {item.label}
                        </span>
                        {item.sublabel && (
                          <span className="block truncate text-xs text-[var(--color-text-muted)]">
                            {item.sublabel}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </EmptySection>
              )}

              {suggestedPages.length > 0 && (
                <EmptySection title="Jump to">
                  {suggestedPages.map((entry) => {
                    const Icon = entry.icon;
                    return (
                      <button
                        key={`suggest:${entry.id}`}
                        type="button"
                        onClick={() =>
                          goRecent({
                            id: entry.id,
                            type: "nav",
                            label: entry.label,
                            sublabel: entry.sublabel,
                            url: entry.url,
                          })
                        }
                        className="w-full min-h-[44px] flex items-center gap-3 px-4 py-2 text-left hover:bg-[var(--color-bg-page)] transition-colors"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-[var(--color-text-primary)]">
                            {entry.label}
                          </span>
                          <span className="block truncate text-xs text-[var(--color-text-muted)]">
                            {entry.sublabel}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
                          {VERB.nav}
                        </span>
                      </button>
                    );
                  })}
                </EmptySection>
              )}

              {topActions.length > 0 && (
                <EmptySection title="Quick actions">
                  {topActions.map((entry) => {
                    const Icon = entry.icon;
                    return (
                      <button
                        key={`topaction:${entry.id}`}
                        type="button"
                        onClick={() => {
                          onClose();
                          entry.run();
                        }}
                        className="w-full min-h-[44px] flex items-center gap-3 px-4 py-2 text-left hover:bg-[var(--color-bg-page)] transition-colors"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-[var(--color-text-primary)]">
                            {entry.label}
                          </span>
                          {entry.sublabel && (
                            <span className="block truncate text-xs text-[var(--color-text-muted)]">
                              {entry.sublabel}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
                          {VERB.action}
                        </span>
                      </button>
                    );
                  })}
                </EmptySection>
              )}
            </div>
          )}

          {/* Federated grouped results. */}
          {!error &&
            !showEmptyState &&
            sections.map((section) => (
              <div key={section.id} className="mb-1" data-section={section.id}>
                <div className="flex items-center justify-between px-4 pt-2 pb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                    {section.title}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {section.seeAll
                      ? section.seeAll.total
                      : section.rows.length}
                  </span>
                </div>
                <ul role="listbox" aria-label={section.title}>
                  {section.rows.map((row) => {
                    runningIndex += 1;
                    const idx = runningIndex;
                    const isActive = idx === activeIndex;
                    const Icon = row.icon;
                    return (
                      <li
                        key={row.key}
                        role="option"
                        aria-selected={isActive}
                      >
                        <button
                          type="button"
                          data-row-index={idx}
                          onMouseEnter={() => setActiveIndex(idx)}
                          onClick={() => activateRow(row)}
                          className={`w-full min-h-[44px] flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                            isActive
                              ? "bg-[var(--color-bg-page)]"
                              : "hover:bg-[var(--color-bg-page)]"
                          }`}
                        >
                          <Icon className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-[var(--color-text-primary)]">
                              {highlight(row.label, row.match)}
                            </span>
                            {row.sublabel && (
                              <span className="block truncate text-xs text-[var(--color-text-muted)]">
                                {row.sublabel}
                              </span>
                            )}
                          </span>
                          {row.typeLabel && (
                            <span
                              data-testid="search-row-type"
                              className="shrink-0 rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-muted)]"
                            >
                              {row.typeLabel}
                            </span>
                          )}
                          <span className="flex shrink-0 items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
                            <span>{VERB[row.kind]}</span>
                            {isActive && (
                              <CornerDownLeft className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {section.seeAll && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(section.seeAll!.url);
                    }}
                    className="w-full min-h-[44px] sm:min-h-[40px] flex items-center gap-2 px-4 py-2 text-left text-xs font-medium text-[var(--color-info)] hover:bg-[var(--color-bg-page)] transition-colors"
                  >
                    See all {section.seeAll.total} in {section.title} →
                  </button>
                )}
              </div>
            ))}

          {/* Skeleton rows while the server request is in flight (no jump). */}
          {loading && scopeWantsServer && (
            <div className="px-4 py-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div
                  key={`skeleton:${i}`}
                  className="flex items-center gap-3 py-2"
                >
                  <div className="h-4 w-4 shrink-0 rounded bg-[var(--color-bg-page)]" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-2/3 rounded bg-[var(--color-bg-page)]" />
                    <div className="h-2.5 w-1/3 rounded bg-[var(--color-bg-page)]" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer hints (desktop only) */}
        <div className="hidden sm:flex items-center gap-4 px-4 py-2 border-t border-[var(--color-border)] text-xs text-[var(--color-text-muted)]">
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-[var(--color-border)] px-1">↑</kbd>
            <kbd className="rounded border border-[var(--color-border)] px-1">↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-[var(--color-border)] px-1">↵</kbd>
            open / run
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-[var(--color-border)] px-1">esc</kbd>
            close
          </span>
          <span className="ml-auto flex items-center gap-1">
            <kbd className="rounded border border-[var(--color-border)] px-1">&gt;</kbd>
            actions
          </span>
        </div>
      </div>
    </div>
  );
}

/** A titled section used only in the rich empty state. */
function EmptySection({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-1">
      <div className="flex items-center justify-between px-4 pt-2 pb-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
